import React, { useEffect, useRef, useState } from "react";
import { Table, Button, Checkbox, message, Select, Input } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import InputNumberField from "../../../../../components/Input/InputNumber";
import SearchListAutoComplete from "../../../../../components/SearchListAutoComplete";
import {
  addOrderRow,
  deleteOrderRow,
  updateOrderRow,
  calculateTotals,
  setFooterValues,
} from "../salesOrderSlice";
import { selectItems } from "../../../../../store/commonSelectors/lookupsSelectors";
import SharedFooter from "../../../../../components/DataTableFooter/SharedFooter";
import { selectAuthInfo } from "../../../../Auth/authSelectors";
import { getCurrencySymbol } from "../../../../../lib/currency";
import { setPayServiceCart } from "../salesOrderSlice";
import { useNavigate } from "react-router-dom";
import ProductQuickDrawer from "../../../../Product_manage/components/drawers/ProductQuickDrawer";
import { fetchItems } from "../../../../../store/commonThunks/lookups";
import { toBig, bigPercent } from "../../../../../lib/bigConversion";
import { getItemDisplayName } from "../../../../../utils";

const SalesOrderTable = ({ quoteId }) => {
  const dispatch = useDispatch();
  const items = useSelector(selectItems);
  const footerValues = useSelector((s) => s.salesOrder.footerValues);
  const itemCart = useSelector((s) => s.salesOrder.itemCart);
  const taxMode = useSelector((s) => s.salesOrder.taxMode);
  const conversionRate = useSelector((s) => s.salesOrder.conversionRate);
  const conversionCurrency = useSelector((s) => s.salesOrder.conversionCurrency);
  const lookups = useSelector((state) => state.lookups);
  const authInfo = useSelector(selectAuthInfo);
  const currency = authInfo?.currency || "AED";
  const symbol = getCurrencySymbol(currency);
  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const isUAE = authInfo?.country === "United Arab Emirates";
  const isDrugStore = authInfo?.company_type === "Drug Store" && isUAE;
  const isPharmacy = authInfo?.company_type === "Pharmacy";
  const taxLabel = isIndia ? "GST" : "VAT";
  const gstEnabled = authInfo?.gst_enabled === "yes";
  const showTax = !isIndia || gstEnabled;

  const [productDrawer, setProductDrawer] = useState({ open: false, rowKey: null });
  const navigate = useNavigate();

  // ✅ Get rate based on party type, with moh_invoice_rate override
  const selectedCustomer = useSelector((s) => s.salesOrder.selectedCustomer || s.customers?.selectedCustomer);

  const getRateForCustomer = (item, partyType, isMohItem = false) => {
    if (isMohItem) {
      return parseFloat(item.moh_invoice_rate) || parseFloat(item.sale_rate) || 0;
    }
    switch (partyType) {
      case 'wholesaler':
        return parseFloat(item.wholesaler_rate) || parseFloat(item.sale_rate) || 0;
      case 'retailer':
        return parseFloat(item.retailer_rate) || parseFloat(item.sale_rate) || 0;
      case 'distributor':
        return parseFloat(item.distributor_rate) || parseFloat(item.sale_rate) || 0;
      case 'corporate':
        return parseFloat(item.corporate_rate) || parseFloat(item.sale_rate) || 0;
      default:
        return parseFloat(item.sale_rate) || 0;
    }
  };

  // ✅ Re-evaluate rates when bonus rows change
  const isRateUpdateInProgress = useRef(false);
const institutionData = useSelector((state) => state.lookups?.institutionData); 
useEffect(() => {
  if (isRateUpdateInProgress.current) return;

  // ✅ If no bonus rows exist, nothing to recalculate — exit immediately
  const bonusItemIds = new Set(
    itemCart
      .filter((row) => row.bonus && row.item_id)
      .map((row) => row.item_id)
  );

  if (bonusItemIds.size === 0) return; // ✅ THIS IS THE FIX

  let needsUpdate = false;

  itemCart.forEach((row) => {
    if (!row.bonus && row.item_id) {
      const shouldUseMoh = bonusItemIds.has(row.item_id);
      const fullProduct = items.find((i) => i.item_id === row.item_id) || {};
      const partyType = selectedCustomer?.party_type || "general";

      const expectedRate = shouldUseMoh
        ? parseFloat(fullProduct.moh_invoice_rate) || parseFloat(fullProduct.sale_rate) || 0
        : getRateForCustomer(fullProduct, partyType, false);

      if (parseFloat(row.rate) !== parseFloat(expectedRate)) {
        needsUpdate = true;
        isRateUpdateInProgress.current = true;

        dispatch(updateOrderRow({
          rowKey: row.key,
          updates: { rate: expectedRate },
        }));
        dispatch(calculateTotals({ authInfo }));
      }
    }
  });

  if (needsUpdate) {
    setTimeout(() => {
      isRateUpdateInProgress.current = false;
    }, 100);
  }
}, [itemCart]);
const handleUpdate = (rowKey, field, value) => {
  const row = itemCart.find((r) => r.key === rowKey);
  const updates = { [field]: value };

  // ✅ Force rate=0 when bonus is checked so edit-load detection works
  if (field === "bonus" && value === true) {
    updates.rate = 0;
  }
  // ✅ Restore rate from items list when bonus is unchecked
  if (field === "bonus" && value === false) {
    const fullProduct = items.find((i) => i.item_id === row.item_id);
    if (fullProduct) {
      updates.rate = parseFloat(fullProduct.sale_rate) || 0;
    }
  }
console.log('instt:::',institutionData)
  if (["quantity", "rate", "discount", "discountAmt", "discountMode", "tax_per", "bonus"].includes(field)) {
    // use updates.rate if rate was just changed above
    const qty = toBig(field === "quantity" ? value : row.quantity) || 0;
    const rate = toBig(updates.rate !== undefined ? updates.rate : (field === "rate" ? value : row.rate)) || 0;

    let line = qty.times(rate);
    if (taxMode === "on_item") {
      const discountMode = (field === "discountMode" ? value : row.discountMode) === "amount" ? "amount" : "percent";
      let discountAmt;
      if (discountMode === "amount") {
        const raw = toBig(field === "discountAmt" ? value : row.discountAmt);
        discountAmt = raw.gt(line) ? line : raw.lt(0) ? toBig(0) : raw;
      } else {
        const discountPct = toBig(field === "discount" ? value : row.discount);
        discountAmt = line.times(discountPct).div(100);
      }
      line = line.minus(discountAmt);
      const taxPer = toBig(field === "tax_per" ? value : row.tax_per);
      const taxAmt = line.times(taxPer).div(100);
      line = line.plus(taxAmt);
    }

    updates.line_total = (
      conversionRate ? line.times(toBig(conversionRate)) : line
    ).toFixed(2);
  }

  dispatch(updateOrderRow({ rowKey, updates }));
  dispatch(calculateTotals({ authInfo }));
};

  const handleAddRow = () => {
    const newRow = {
      key: Date.now(),
      item_id: null,
      product: "",
      quantity: null,
      rate: 0,
      tax_per: 0,
      discount: null,
      discountAmt: null,
      discountMode: "percent",
      bonus: false,
      item_description: "",
      showDescription: false,
    };
    dispatch(addOrderRow(newRow));
  };

  const handleFooterChange = (value, field) => {
    let updatedFooter = { ...footerValues };

    if (field === "payServiceCart") {
      dispatch(setPayServiceCart(value));
      dispatch(calculateTotals({ authInfo }));
      return;
    }

    if (field === "payCart") {
      const newPaidAmount = value.reduce(
        (sum, item) => sum + parseFloat(item.amount || 0),
        0
      );
      updatedFooter = {
        ...updatedFooter,
        payCart: value,
        paid_amount: newPaidAmount,
        paidAmount: newPaidAmount,
        dueAmount: (
          parseFloat(updatedFooter.grandTotal || 0) - newPaidAmount
        ).toFixed(2),
      };
    } else if (field === "paidAmount") {
      updatedFooter.paidAmount = Number(value) || 0;
      updatedFooter.dueAmount = (
        parseFloat(updatedFooter.grandTotal || 0) - updatedFooter.paidAmount
      ).toFixed(2);
    } else {
      updatedFooter[field] = value;
    }

    dispatch(setFooterValues(updatedFooter));
    dispatch(calculateTotals({ authInfo }));
  };
const RateSelector = ({ record, handleUpdate, lookups, conversionRate, quoteId }) => {
  const [open, setOpen] = useState(false);
  const warnedRef = useRef(false);

  const allowEdit = lookups?.institutionData?.allow_product_rate_change === "yes";
  const isFullyLocked = !record?.product || !!quoteId;
  const isManualEditAllowed = allowEdit && !isFullyLocked;

  const displayRate = record.bonus
    ? 0
    : conversionRate
    ? (Number(record?.rate) || 0) * Number(conversionRate)
    : Number(record?.rate) || 0;
  console.log("RateSelector debug:", {
    allowEdit,
    isFullyLocked,
    isManualEditAllowed,
    institutionData: lookups?.institutionData,
    allow_product_rate_change: lookups?.institutionData?.allow_product_rate_change,
    product: record?.product,
    quoteId,
  });
  return (
    <InputNumberField
      min={0}
      step={0.01}
      precision={2}
      value={displayRate}
      disabled={isFullyLocked}
      onChange={(value) => {
        if (isManualEditAllowed) {
          handleUpdate(record.key, "rate", value);
        }
      }}
      onKeyDown={(e) => {
        if (!isManualEditAllowed) {
          e.preventDefault();
          if (!warnedRef.current) {
            message.warning("Enable 'Allow Product Rate Edit' in settings to edit manually");
            warnedRef.current = true;
          }
        }
      }}
      onBlur={() => { warnedRef.current = false; }}
      style={{
        width: "100%",
        cursor: isFullyLocked ? "not-allowed" : isManualEditAllowed ? "text" : "pointer",
        background: isManualEditAllowed ? "white" : "#fafafa",
        color: isManualEditAllowed ? "inherit" : "#888",
        caretColor: isManualEditAllowed ? "auto" : "transparent",
      }}
    />
  );
};
  const baseColumns = [
    {
      title: "Bonus",
      dataIndex: "bonus",
      width: 50,
      render: (_, record) => (
        <Checkbox
          checked={record.bonus}
          onChange={(e) => {
            handleUpdate(record.key, "bonus", e.target.checked);
            handleUpdate(record.key, "product", null);
            dispatch(
              updateOrderRow({
                rowKey: record.key,
                updates: {
                  qty: null,
                  discount: 0,
                  tax_per: 0,
                  unit_symbol: "",
                  units: [],
                  conversion: 1,
                  per_conversion: 1,
                  quantity: null,
                  warehouse_id: null,
                },
              })
            );
            dispatch(calculateTotals({ authInfo }));
          }}
        />
      ),
    },
    {
      title: "Item",
      dataIndex: "product",
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <SearchListAutoComplete
          entityType="item"
          placeholder="Item"
          style={{ width: "100%" }}
          showAddNew={true}
          optionsList={items
            // Asset items are Purchase Bill-only (non-stock) -- never offer for sale.
            .filter((i) => i.is_asset !== "yes")
            .filter(
              (i) =>
                !itemCart.some(
                  (r) =>
                    r.key !== record.key &&
                    r.item_id === i.item_id &&
                    Boolean(r.bonus) === Boolean(record.bonus)
                )
            )
            .map((i) => ({
              sale_id: i.item_id,
              display_text: getItemDisplayName(i),
              raw: i,
            }))}
          value={
            record.item_id
              ? { value: record.product, label: record.product }
              : undefined
          }
          onSelect={async (obj) => {
            if (!obj) {
              dispatch(
                updateOrderRow({
                  rowKey: record.key,
                  updates: {
                    item_id: null,
                    product: "",
                    qty: null,
                    rate: null,
                    discount: 0,
                    tax_per: 0,
                    unit_symbol: "",
                    units: [],
                    conversion: 1,
                    per_conversion: 1,
                    quantity: null,
                    bonus: false,
                    warehouse_id: null,
                    item_description: "",
                    showDescription: false,
                  },
                })
              );
              dispatch(calculateTotals({ authInfo }));
              return;
            }

            if (obj === "add_new") {
              setProductDrawer({ open: true, rowKey: record.key });
              return;
            }

            const it = items.find((i) => i.item_id == obj?.sale_id) || obj?.raw;
            console.log(obj, it, "obj");

            const partyType = selectedCustomer?.party_type || "general";

            // ✅ Check if this product already exists as a BONUS row
            const existsAsBonus = itemCart.some(
              (row) =>
                row.key !== record.key &&
                row.item_id === it.item_id &&
                row.bonus === true
            );

            // ✅ Get rate: moh_invoice_rate if bonus exists, else party-type rate
            const baseRate = getRateForCustomer(it, partyType, existsAsBonus);

            handleUpdate(record.key, "item_id", it.item_id);
            handleUpdate(record.key, "product", getItemDisplayName(it));
            handleUpdate(record.key, "rate", baseRate);
            handleUpdate(record.key, "tax_per", it.tax_per ?? 0);

            const narration = String(it.narration ?? "").trim();
            handleUpdate(record.key, "item_description", narration);
            handleUpdate(record.key, "showDescription", !!narration);

            if ((isDrugStore || isPharmacy) && parseFloat(it.discount_per || 0) > 0) {
              handleUpdate(record.key, "discount", parseFloat(it.discount_per));
            } else {
              handleUpdate(record.key, "discount", 0);
            }
          }}
        />
        {record.item_id && (
          <Button
            type="link"
            size="small"
            style={{ padding: 0, alignSelf: "flex-start" }}
            onClick={() =>
              handleUpdate(record.key, "showDescription", !record.showDescription)
            }
          >
            {record.showDescription ? "Hide Description" : "Add Description"}
          </Button>
        )}
        {record.showDescription && (
          <Input.TextArea
            rows={2}
            placeholder="Add item description"
            value={record.item_description}
            onChange={(e) =>
              handleUpdate(record.key, "item_description", e.target.value)
            }
          />
        )}
        </div>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      width: 100,
      render: (_, record) => (
        <InputNumberField
          min={0}
          precision={1}
          step={0.5}
          value={record.quantity}
           onClick={() => {
            if (record.quantity !== null) {
              handleUpdate(record.key, "quantity", null);
            }
          }}
          onChange={(v) => handleUpdate(record.key, "quantity", v)}
          disabled={record.isFromQuotation}
        />
      ),
    },
{
  title: "Rate",
  dataIndex: "rate",
  width: 120,
  render: (_, record) => (
    <RateSelector
      key={`${record.key}-${conversionRate}`}
      record={record}
      handleUpdate={handleUpdate}
      lookups={lookups}        // ✅ already in your file: const lookups = useSelector((state) => state.lookups);
      conversionRate={conversionRate}
      quoteId={quoteId}
    />
  ),
},
  ];

  const individualColumns = [
    {
      title: "Discount",
      dataIndex: "discount",
      width: 160,
      render: (_, record) => {
        const mode = record.discountMode === "amount" ? "amount" : "percent";
        const lineTotal = toBig(record.quantity).times(toBig(record.rate));
        // One merged compound field (number input + unit-select addon)
        // instead of a separate Select stacked above the number input.
        const modeSelect = (
          <Select
            size="small"
            style={{ width: 65 }}
            disabled={record.bonus}
            value={mode}
            options={[
              { label: "%", value: "percent" },
              { label: symbol, value: "amount" },
            ]}
            onChange={(v) => handleUpdate(record.key, "discountMode", v)}
          />
        );
        return mode === "amount" ? (
          <InputNumberField
            min={0}
            max={Number(lineTotal)}
            disabled={record.bonus}
            placeholder="0"
            value={record.discountAmt}
            onChange={(v) => handleUpdate(record.key, "discountAmt", v)}
            addonAfter={modeSelect}
          />
        ) : (
          <InputNumberField
            min={0}
            max={100}
            disabled={record.bonus}
            placeholder="0"
            value={record.discount}
            onClick={() => {
              if (record.discount !== null) {
                handleUpdate(record.key, "discount", null);
              }
            }}
            onChange={(v) => handleUpdate(record.key, "discount", v)}
            addonAfter={modeSelect}
          />
        );
      },
    },
  ];

  const gstColumn = showTax
    ? [
      {
        title: `${taxLabel} %`,
        dataIndex: "tax_per",
        width: 120,
        render: (_, record) => (
          <InputNumberField
            min={0}
            value={record.tax_per}
            onChange={(v) => handleUpdate(record.key, "tax_per", v)}
            disabled
          />
        ),
      },
    ]
    : [];

  const amountColumn = [
    {
      title: "Line Total",
      render: (_, r) => {
        if (r.bonus) return "0.00";

        const qty = toBig(r.quantity);
        const rate = toBig(r.rate);
        let line = qty.times(rate);

        if (taxMode === "on_item") {
          const discountMode = r.discountMode === "amount" ? "amount" : "percent";
          let discountAmt;
          if (discountMode === "amount") {
            const raw = toBig(r.discountAmt);
            discountAmt = raw.gt(line) ? line : raw.lt(0) ? toBig(0) : raw;
          } else {
            discountAmt = line.times(toBig(r.discount)).div(100) || 0;
          }
          line = line.minus(discountAmt);
          const taxAmt = showTax ? line.times(toBig(r.tax_per)).div(100) : toBig(0);
          line = line.plus(taxAmt);
        }
        const convertedLine = parseFloat(
          conversionRate ? line.times(toBig(conversionRate)) : line
        ).toFixed(2);
        return convertedLine;
      },
    },
    {
      title: "",
      render: (_, r) => (
        <Button
          icon={<DeleteOutlined />}
          danger
          onClick={() => {
            dispatch(deleteOrderRow(r.key));
            dispatch(calculateTotals({ authInfo }));
          }}
          disabled={itemCart.length === 1}
        />
      ),
    },
  ];

  const columns =
    taxMode === "on_item"
      ? [...baseColumns, ...individualColumns, ...gstColumn, ...amountColumn]
      : [...baseColumns, ...amountColumn];

  return (
    <div>
      <Button
        type="dashed"
        onClick={handleAddRow}
        icon={<PlusOutlined />}
        style={{ marginBottom: 8 }}
      >
        Add Row
      </Button>
      <div style={{ width: "100%", maxWidth: "1300px", overflowX: "auto" }}>
        <Table
          dataSource={itemCart}
          columns={columns}
          pagination={false}
          rowKey="key"
          scroll={{ x: "max-content" }}
          footer={() => (
            <SharedFooter
              footerValues={footerValues}
              dataSource={itemCart}
              onFooterChange={handleFooterChange}
              pageType="salesOrder"
              taxMode={taxMode}
              conversionRate={conversionRate}
              conversionCurrency={conversionCurrency}
            />
          )}
        />
      </div>
      <ProductQuickDrawer
        open={productDrawer.open}
        onClose={() => setProductDrawer({ open: false, rowKey: null })}
        onCreated={(product) => {
          if (!product) return;

          dispatch(fetchItems(""));

          const it = {
            ...product,
            item_id: product.item_id,
            item_name: product.item_name,
            display_text: getItemDisplayName(product),
            tax_per: product.tax_per,
            sale_rate: product.sale_rate,
            moh_invoice_rate: product.moh_invoice_rate,
          };

          const partyType = selectedCustomer?.party_type || "general";

          // ✅ Check bonus for newly created product too
          const existsAsBonus = itemCart.some(
            (row) =>
              row.key !== productDrawer.rowKey &&
              row.item_id === it.item_id &&
              row.bonus === true
          );

          const baseRate = getRateForCustomer(it, partyType, existsAsBonus);

          handleUpdate(productDrawer.rowKey, "item_id", it.item_id);
          handleUpdate(productDrawer.rowKey, "product", getItemDisplayName(it));
          handleUpdate(productDrawer.rowKey, "rate", baseRate);
          handleUpdate(productDrawer.rowKey, "tax_per", it.tax_per ?? 0);

          const narration = String(it.narration ?? "").trim();
          handleUpdate(productDrawer.rowKey, "item_description", narration);
          handleUpdate(productDrawer.rowKey, "showDescription", !!narration);

          if ((isDrugStore || isPharmacy) && parseFloat(it.discount_per || 0) > 0) {
            handleUpdate(productDrawer.rowKey, "discount", parseFloat(it.discount_per));
          } else {
            handleUpdate(productDrawer.rowKey, "discount", 0);
          }
        }}
      />
    </div>
  );
};

export default SalesOrderTable;